import React, { useState, useMemo } from "react";
import {
  Settings,
  Save,
  Trash2,
  Plus,
  Clock,
  Calendar,
  User,
  Check,
  X,
  Stethoscope,
} from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { useAgenda, ConfigAgendaProfissional } from "../context/AgendaContext";
import { useUsuarios } from "../../usuarios/context/UsuariosContext";
import { CATEGORIA_LABELS } from "../../../types";

const DIAS_SEMANA = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda-feira" },
  { value: 2, label: "Terça-feira" },
  { value: 3, label: "Quarta-feira" },
  { value: 4, label: "Quinta-feira" },
  { value: 5, label: "Sexta-feira" },
  { value: 6, label: "Sábado" },
];

export default function ConfiguracaoAgenda() {
  const { usuarioLogado } = useAuth();
  const {
    configsProfissionais,
    salvarConfigProfissional,
    excluirConfigProfissional,
    agendamentos,
    atualizarAgendamento,
  } = useAgenda();
  const { usuarios, carregarUsuarios } = useUsuarios();

  React.useEffect(() => {
    if (usuarios.length === 0) {
      carregarUsuarios();
    }
  }, []);

  const [editando, setEditando] = useState<string | null>(null);
  const [novaConfig, setNovaConfig] = useState(false);

  // Estado do formulário
  const [form, setForm] = useState<{
    profissionalId: string;
    profissionalNome: string;
    profissionalCategoria: string;
    duracaoConsulta: number;
    tipoAgenda: "fixa" | "livre";
    diasFixos: {
      id: string;
      diaSemana: number;
      horaInicio: string;
      horaFim: string;
    }[];
    horaInicioDefault: string;
    horaFimDefault: string;
  }>({
    profissionalId: "",
    profissionalNome: "",
    profissionalCategoria: "",
    duracaoConsulta: 30,
    tipoAgenda: "fixa",
    diasFixos: [],
    horaInicioDefault: "08:00",
    horaFimDefault: "17:00",
  });

  const isAdmin = usuarioLogado?.tipo === "administrador";
  const podeConfigurar =
    isAdmin ||
    usuarioLogado?.categoria === "enfermeiro" ||
    usuarioLogado?.categoria === "administrativo";

  // Profissionais que podem ter agenda (médicos, psicólogos, nutricionistas)
  const profissionaisDisponiveis = useMemo(() => {
    return usuarios.filter(
      (u) =>
        u.ativo &&
        ["medico", "psicologo", "nutricionista"].includes(u.categoria)
    );
  }, [usuarios]);

  // ============================================
  // AÇÕES
  // ============================================
  const iniciarNova = () => {
    setForm({
      profissionalId: "",
      profissionalNome: "",
      profissionalCategoria: "",
      duracaoConsulta: 30,
      tipoAgenda: "fixa",
      diasFixos: [],
      horaInicioDefault: "08:00",
      horaFimDefault: "17:00",
    });
    setNovaConfig(true);
    setEditando(null);
  };

  const editarConfig = (config: ConfigAgendaProfissional) => {
    setForm({
      profissionalId: config.profissionalId,
      profissionalNome: config.profissionalNome,
      profissionalCategoria: config.profissionalCategoria,
      duracaoConsulta: config.duracaoConsulta,
      tipoAgenda: config.tipoAgenda,
      diasFixos: (config.diasFixos || []).map((d, i) => ({
        ...d,
        id: d.id || Date.now().toString() + i,
      })),
      horaInicioDefault: config.horaInicioDefault || "08:00",
      horaFimDefault: config.horaFimDefault || "17:00",
    });
    setEditando(config.id);
    setNovaConfig(false);
  };

  const selecionarProfissional = (id: string) => {
    const prof = profissionaisDisponiveis.find((p) => p.id === id);
    if (prof) {
      setForm((prev) => ({
        ...prev,
        profissionalId: prof.id,
        profissionalNome: prof.nome,
        profissionalCategoria: prof.categoria,
      }));
    }
  };

  const adicionarDiaFixo = () => {
    setForm((prev) => ({
      ...prev,
      diasFixos: [
        ...prev.diasFixos,
        {
          id: Date.now().toString(),
          diaSemana: 1,
          horaInicio: "08:00",
          horaFim: "12:00",
        },
      ].sort(
        (a, b) =>
          a.diaSemana - b.diaSemana || a.horaInicio.localeCompare(b.horaInicio)
      ),
    }));
  };

  const removerDiaFixo = (id: string) => {
    setForm((prev) => ({
      ...prev,
      diasFixos: prev.diasFixos.filter((d) => d.id !== id),
    }));
  };

  const atualizarDiaFixo = (
    id: string,
    campo: "diaSemana" | "horaInicio" | "horaFim",
    valor: string | number
  ) => {
    setForm((prev) => ({
      ...prev,
      diasFixos: prev.diasFixos
        .map((d) => (d.id === id ? { ...d, [campo]: valor } : d))
        .sort(
          (a, b) =>
            a.diaSemana - b.diaSemana ||
            a.horaInicio.localeCompare(b.horaInicio)
        ),
    }));
  };

  const calcularVagasPeriodo = (
    horaInicio: string,
    horaFim: string,
    duracao: number
  ): number => {
    const [hI, mI] = horaInicio.split(":").map(Number);
    const [hF, mF] = horaFim.split(":").map(Number);
    const minutos = hF * 60 + mF - (hI * 60 + mI);
    return Math.floor(minutos / duracao);
  };

  const handleSalvar = async () => {
    if (!form.profissionalId) {
      alert("Selecione um profissional.");
      return;
    }
    if (form.tipoAgenda === "fixa" && form.diasFixos.length === 0) {
      alert("Adicione pelo menos um dia de atendimento.");
      return;
    }
    // Verificar duplicatas de dia + período sobreposto
    if (form.tipoAgenda === "fixa") {
      for (let i = 0; i < form.diasFixos.length; i++) {
        for (let j = i + 1; j < form.diasFixos.length; j++) {
          const a = form.diasFixos[i];
          const b = form.diasFixos[j];
          if (a.diaSemana === b.diaSemana) {
            // Verificar sobreposição de horário
            if (a.horaInicio < b.horaFim && b.horaInicio < a.horaFim) {
              const diaNome = [
                "Domingo",
                "Segunda",
                "Terça",
                "Quarta",
                "Quinta",
                "Sexta",
                "Sábado",
              ][a.diaSemana];
              alert(
                `Conflito de horário em ${diaNome}: ${a.horaInicio}-${a.horaFim} sobrepõe com ${b.horaInicio}-${b.horaFim}. Ajuste os períodos.`
              );
              return;
            }
          }
        }
      }
    }
    try {
      // Verificar se está editando e se há agendamentos afetados
      if (editando && form.tipoAgenda === "fixa") {
        const hoje = new Date().toISOString().split("T")[0];
        const novosDias = form.diasFixos.map((d) => d.diaSemana);

        // Buscar agendamentos futuros deste profissional
        const agendamentosFuturos = agendamentos.filter(
          (a) =>
            a.profissionalId === form.profissionalId &&
            a.status === "agendado" &&
            a.data >= hoje
        );

        // Encontrar agendamentos em dias que não existem mais na nova configuração
        const afetados = agendamentosFuturos.filter((a) => {
          const diaSemana = new Date(a.data + "T00:00:00").getDay();
          return !novosDias.includes(diaSemana);
        });

        if (afetados.length > 0) {
          const confirma = window.confirm(
            `Atenção: ${afetados.length} agendamento(s) estão marcados em dias que não fazem mais parte da nova escala.\n\n` +
              `Deseja mover estes pacientes para a próxima data disponível na nova escala?\n\n` +
              `Clique em OK para remarcar automaticamente ou Cancelar para voltar e ajustar.`
          );

          if (!confirma) return;

          // Remarcar automaticamente para próxima data disponível
          for (const ag of afetados) {
            // Buscar próxima data disponível a partir da data original
            let novaData = new Date(ag.data + "T00:00:00");
            let encontrou = false;

            for (let tentativa = 0; tentativa < 60; tentativa++) {
              novaData.setDate(novaData.getDate() + 1);
              const diaSem = novaData.getDay();
              if (novosDias.includes(diaSem)) {
                const novaDataISO = `${novaData.getFullYear()}-${String(
                  novaData.getMonth() + 1
                ).padStart(2, "0")}-${String(novaData.getDate()).padStart(
                  2,
                  "0"
                )}`;
                await atualizarAgendamento(ag.id, {
                  data: novaDataISO,
                  observacao:
                    (ag.observacao ? ag.observacao + " | " : "") +
                    `Remarcado automaticamente de ${ag.data
                      .split("-")
                      .reverse()
                      .join("/")}`,
                });
                encontrou = true;
                break;
              }
            }

            if (!encontrou) {
              console.warn(`Não encontrou nova data para ${ag.pacienteNome}`);
            }
          }

          alert(
            `${afetados.length} agendamento(s) foram remarcados automaticamente.`
          );
        }
      }

      const dados: any = {
        profissionalId: form.profissionalId,
        profissionalNome: form.profissionalNome,
        profissionalCategoria: form.profissionalCategoria,
        duracaoConsulta: form.duracaoConsulta,
        tipoAgenda: form.tipoAgenda,
        ativo: true,
      };
      if (form.tipoAgenda === "fixa") {
        dados.diasFixos = form.diasFixos;
        dados.horaInicioDefault = null;
        dados.horaFimDefault = null;
      } else {
        dados.diasFixos = null;
        dados.horaInicioDefault = form.horaInicioDefault || "08:00";
        dados.horaFimDefault = form.horaFimDefault || "17:00";
      }
      await salvarConfigProfissional(dados);
      setNovaConfig(false);
      setEditando(null);
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      alert("Erro ao salvar.");
    }
  };

  const handleExcluir = async (id: string) => {
    const config = configsProfissionais.find((c) => c.id === id);
    if (!config) return;

    // Verificar se existem agendamentos pendentes
    const pendentes = agendamentos.filter(
      (a) =>
        a.profissionalId === config.profissionalId &&
        a.status === "agendado" &&
        a.data >= new Date().toISOString().split("T")[0]
    );

    if (pendentes.length > 0) {
      alert(
        `Não é possível remover esta configuração. Existem ${pendentes.length} agendamento(s) pendente(s) para ${config.profissionalNome}. Remarque ou cancele os agendamentos primeiro.`
      );
      return;
    }

    if (!window.confirm("Remover esta configuração de agenda?")) return;
    try {
      await excluirConfigProfissional(id);
    } catch (error) {
      console.error("Erro ao excluir:", error);
    }
  };

  if (!podeConfigurar) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "32px",
          color: theme.gray[500],
          fontFamily: theme.fonts.sans,
        }}
      >
        Apenas administradores, enfermeiros e administrativos podem configurar
        agendas.
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: "14px",
            fontWeight: "700",
            color: theme.gray[700],
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Settings size={16} />
          {configsProfissionais.length} profissional(is) configurado(s)
        </span>
        <button
          onClick={iniciarNova}
          style={{
            padding: "6px 14px",
            background: theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "12px",
            fontWeight: "600",
            cursor: "pointer",
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Plus size={14} /> Configurar Profissional
        </button>
      </div>

      {/* FORMULÁRIO */}
      {(novaConfig || editando) && (
        <div
          style={{
            padding: "16px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: theme.borderRadius.lg,
          }}
        >
          <h3
            style={{
              fontSize: "14px",
              fontWeight: "700",
              color: theme.primary[700],
              fontFamily: theme.fonts.sans,
              marginBottom: "12px",
            }}
          >
            {editando ? "Editar Configuração" : "Nova Configuração de Agenda"}
          </h3>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {/* PROFISSIONAL */}
            {!editando && (
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Profissional
                </label>
                <select
                  value={form.profissionalId}
                  onChange={(e) => selecionarProfissional(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "13px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <option value="">Selecione um profissional...</option>
                  {profissionaisDisponiveis
                    .filter(
                      (p) =>
                        !configsProfissionais.some(
                          (c) => c.profissionalId === p.id
                        ) || editando
                    )
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome} —{" "}
                        {CATEGORIA_LABELS[p.categoria] || p.categoria}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {form.profissionalId && (
              <>
                {/* DURAÇÃO E TIPO */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Duração da consulta
                    </label>
                    <select
                      value={form.duracaoConsulta}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          duracaoConsulta: Number(e.target.value),
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "13px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <option value={15}>15 minutos</option>
                      <option value={20}>20 minutos</option>
                      <option value={30}>30 minutos</option>
                      <option value={45}>45 minutos</option>
                      <option value={60}>60 minutos</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Tipo de agenda
                    </label>
                    <select
                      value={form.tipoAgenda}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          tipoAgenda: e.target.value as "fixa" | "livre",
                        }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "13px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <option value="fixa">
                        Dias fixos (ex: toda segunda à tarde)
                      </option>
                      <option value="livre">Livre (sem escala fixa)</option>
                    </select>
                  </div>
                </div>

                {/* AGENDA FIXA — DIAS */}
                {form.tipoAgenda === "fixa" && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: theme.gray[500],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Dias de atendimento
                      </label>
                      {form.diasFixos.length < 14 && (
                        <button
                          onClick={adicionarDiaFixo}
                          style={{
                            padding: "3px 8px",
                            background: theme.primary[100],
                            color: theme.primary[700],
                            border: "none",
                            borderRadius: theme.borderRadius.sm,
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontFamily: theme.fonts.sans,
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                          }}
                        >
                          <Plus size={10} /> Dia
                        </button>
                      )}
                    </div>
                    {form.diasFixos.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "12px",
                          background: theme.gray[50],
                          borderRadius: theme.borderRadius.sm,
                          fontSize: "12px",
                          color: theme.gray[400],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Clique em "+ Dia" para adicionar dias de atendimento
                      </div>
                    )}
                    {form.diasFixos.map((dia) => {
                      const vagas = calcularVagasPeriodo(
                        dia.horaInicio,
                        dia.horaFim,
                        form.duracaoConsulta
                      );
                      return (
                        <div
                          key={dia.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "6px",
                            padding: "6px 8px",
                            background: "white",
                            borderRadius: theme.borderRadius.sm,
                            border: `1px solid ${theme.gray[200]}`,
                          }}
                        >
                          <select
                            value={dia.diaSemana}
                            onChange={(e) =>
                              atualizarDiaFixo(
                                dia.id,
                                "diaSemana",
                                Number(e.target.value)
                              )
                            }
                            style={{
                              padding: "4px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.sm,
                              fontSize: "12px",
                              fontFamily: theme.fonts.sans,
                              flex: 1,
                            }}
                          >
                            {DIAS_SEMANA.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                          <span
                            style={{ fontSize: "11px", color: theme.gray[500] }}
                          >
                            das
                          </span>
                          <input
                            type="time"
                            value={dia.horaInicio}
                            onChange={(e) =>
                              atualizarDiaFixo(
                                dia.id,
                                "horaInicio",
                                e.target.value
                              )
                            }
                            style={{
                              padding: "4px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.sm,
                              fontSize: "12px",
                            }}
                          />
                          <span
                            style={{ fontSize: "11px", color: theme.gray[500] }}
                          >
                            às
                          </span>
                          <input
                            type="time"
                            value={dia.horaFim}
                            onChange={(e) =>
                              atualizarDiaFixo(
                                dia.id,
                                "horaFim",
                                e.target.value
                              )
                            }
                            style={{
                              padding: "4px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.sm,
                              fontSize: "12px",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              color: theme.primary[600],
                              fontFamily: theme.fonts.sans,
                              minWidth: "55px",
                              textAlign: "center",
                            }}
                          >
                            {vagas} vaga{vagas !== 1 ? "s" : ""}
                          </span>
                          <button
                            onClick={() => removerDiaFixo(dia.id)}
                            style={{
                              padding: "3px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* AGENDA LIVRE — sem escala fixa */}
                {form.tipoAgenda === "livre" && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: theme.gray[500],
                      fontFamily: theme.fonts.sans,
                      padding: "8px",
                      background: theme.gray[50],
                      borderRadius: theme.borderRadius.sm,
                    }}
                  >
                    Profissional sem escala fixa. Após salvar, vá em{" "}
                    <strong>Programação Mensal</strong> para definir os dias e
                    horários de atendimento de cada mês.
                  </div>
                )}
              </>
            )}
          </div>

          {/* BOTÕES */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "12px",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => {
                setNovaConfig(false);
                setEditando(null);
              }}
              style={{
                padding: "6px 12px",
                background: theme.gray[300],
                color: theme.gray[700],
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <X size={14} /> Cancelar
            </button>
            <button
              onClick={handleSalvar}
              style={{
                padding: "6px 16px",
                background: theme.primary[600],
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Save size={14} /> Salvar
            </button>
          </div>
        </div>
      )}

      {/* LISTA DE CONFIGURAÇÕES */}
      {configsProfissionais.length === 0 && !novaConfig ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            color: theme.gray[400],
            fontFamily: theme.fonts.sans,
          }}
        >
          <Settings
            size={40}
            color={theme.gray[300]}
            style={{ marginBottom: "8px" }}
          />
          <p>Nenhum profissional configurado</p>
          <p style={{ fontSize: "12px" }}>
            Configure as agendas dos profissionais para começar a agendar
            consultas
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {configsProfissionais.map((config) => {
            const totalVagasSemana =
              config.tipoAgenda === "fixa"
                ? (config.diasFixos || []).reduce(
                    (acc, d) =>
                      acc +
                      calcularVagasPeriodo(
                        d.horaInicio,
                        d.horaFim,
                        config.duracaoConsulta
                      ),
                    0
                  )
                : calcularVagasPeriodo(
                    config.horaInicioDefault || "08:00",
                    config.horaFimDefault || "17:00",
                    config.duracaoConsulta
                  );

            return (
              <div
                key={config.id}
                style={{
                  padding: "12px",
                  background: config.ativo ? "white" : theme.gray[50],
                  border: `1px solid ${
                    config.ativo ? theme.gray[200] : theme.gray[300]
                  }`,
                  borderRadius: theme.borderRadius.md,
                  borderLeft: `4px solid ${
                    config.ativo ? theme.primary[500] : theme.gray[400]
                  }`,
                  opacity: config.ativo ? 1 : 0.6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: theme.gray[800],
                        fontFamily: theme.fonts.sans,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Stethoscope size={14} color={theme.primary[600]} />
                      {config.profissionalNome}
                      <span
                        style={{
                          fontSize: "10px",
                          background: theme.primary[100],
                          color: theme.primary[700],
                          padding: "1px 6px",
                          borderRadius: "8px",
                        }}
                      >
                        {CATEGORIA_LABELS[
                          config.profissionalCategoria as keyof typeof CATEGORIA_LABELS
                        ] || config.profissionalCategoria}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: theme.gray[500],
                        fontFamily: theme.fonts.sans,
                        marginTop: "4px",
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      <span>
                        <Clock size={10} /> {config.duracaoConsulta}{" "}
                        min/consulta
                      </span>
                      <span>
                        <Calendar size={10} />{" "}
                        {config.tipoAgenda === "fixa"
                          ? `${(config.diasFixos || []).length} dia(s) fixo(s)`
                          : "Agenda livre"}
                      </span>
                      <span
                        style={{ color: theme.primary[600], fontWeight: "600" }}
                      >
                        {config.tipoAgenda === "fixa"
                          ? `${totalVagasSemana} vagas/semana`
                          : `${totalVagasSemana} vagas/dia`}
                      </span>
                    </div>
                    {config.tipoAgenda === "fixa" && config.diasFixos && (
                      <div
                        style={{
                          fontSize: "10px",
                          color: theme.gray[400],
                          marginTop: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {config.diasFixos
                          .map(
                            (d) =>
                              `${DIAS_SEMANA.find(
                                (ds) => ds.value === d.diaSemana
                              )?.label?.substring(0, 3)} ${d.horaInicio}-${
                                d.horaFim
                              }`
                          )
                          .join(" | ")}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={() => editarConfig(config)}
                      style={{
                        padding: "4px 8px",
                        background: theme.gray[100],
                        color: theme.gray[600],
                        border: "none",
                        borderRadius: theme.borderRadius.sm,
                        fontSize: "11px",
                        cursor: "pointer",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleExcluir(config.id)}
                      style={{
                        padding: "4px",
                        background: "#fee2e2",
                        color: "#dc2626",
                        border: "none",
                        borderRadius: theme.borderRadius.sm,
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
